<template>
  <div class="application-component invent-idea-component" :class="{ compact }">
    <div class="application-component-title">发明构思</div>
    <div
      class="application-component-content invent-idea-content"
      :style="{ fontSize }"
      v-loading="!readOnly && generating"
      element-loading-text="正在生成发明构思..."
    >
      <template v-if="readOnly">
        <el-skeleton :rows="5" animated />
      </template>
      <template v-else>
        <div v-if="hasMap" class="idea-toolbar">
          <el-button type="primary" size="small" :loading="generating" @click="handleGenerate">
            重新生成
          </el-button>
        </div>

        <div v-if="hasMap" class="idea-block-list">
          <section
            v-for="(block, index) in ideaBlocks"
            :key="block.id"
            class="idea-block"
            :class="`idea-block-${block.variant}`"
          >
            <div class="idea-block-head">
              <span class="idea-block-index">{{ index + 1 }}</span>
              <span class="idea-block-title">{{ block.variant === 'primary' ? '核心构思' : '补充构思' }}</span>
            </div>
            <dl class="idea-field-list">
              <div class="idea-field">
                <dt>{{ block.problemLabel }}</dt>
                <dd>{{ block.problem || '暂无内容' }}</dd>
              </div>
              <div class="idea-field">
                <dt>{{ block.measureLabel }}</dt>
                <dd>{{ block.measure || '暂无内容' }}</dd>
              </div>
              <div class="idea-field">
                <dt>{{ block.effectLabel }}</dt>
                <dd>{{ block.effect || '暂无内容' }}</dd>
              </div>
            </dl>
          </section>
        </div>

        <div v-else class="idea-empty">
          <el-empty>
            <template #description>
              <div class="idea-empty-copy">
                <div class="idea-empty-title">首次进入会自动生成发明构思</div>
                <div class="idea-empty-desc">如果自动生成失败，可以在这里重新触发。</div>
              </div>
            </template>
            <el-button type="primary" size="small" :loading="generating" @click="handleGenerate">
              生成发明构思
            </el-button>
          </el-empty>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ElMessage } from 'element-plus'
import { computed, ref, watch } from 'vue'
import { useGlobalStore } from '@/store/modules/global'
import { useWritingStore } from '@/store/modules/writing'
import { fetchGenerateInventiveIdea } from '@/api/writing'
import { applicationDataMap } from '../index'
import {
  buildInventIdeaTextBlocks,
  hasInventIdeaLogicMap,
  normalizeInventIdeaLogicMap,
} from './inventIdeaLogicMap.mjs'

const COMPONENT_TYPE_CODE = applicationDataMap.InventIdea

const globalStore = useGlobalStore()
const writingStore = useWritingStore()

const props = defineProps({
  readOnly: {
    type: Boolean,
    default: false,
  },
  data: {
    type: Object,
    default: () => null,
  },
  projectId: {
    type: String,
    default: '',
  },
  compact: {
    type: Boolean,
    default: false,
  },
})

const generating = ref(false)
const hasAutoTriggered = ref(false)
const localDocResult = ref('')

const fontSize = computed(() => `${globalStore.fontSize}px`)

const docResultRaw = computed(() => {
  if (localDocResult.value) {
    return localDocResult.value
  }
  const fromProps = props.data?.docResult
  if (fromProps !== undefined && fromProps !== null && fromProps !== '') {
    return fromProps
  }
  return writingStore.step2Info?.step2Component?.[COMPONENT_TYPE_CODE]?.docResult || ''
})

const logicMap = computed(() => normalizeInventIdeaLogicMap(docResultRaw.value))
const ideaBlocks = computed(() => buildInventIdeaTextBlocks(logicMap.value))
const hasMap = computed(() => hasInventIdeaLogicMap(logicMap.value))

const syncGeneratedResult = (result) => {
  const docResult = JSON.stringify(result || {})
  localDocResult.value = docResult

  const nextStep2Info = {
    ...(writingStore.step2Info || {}),
    step2Component: {
      ...(writingStore.step2Info?.step2Component || {}),
      [COMPONENT_TYPE_CODE]: {
        ...(writingStore.step2Info?.step2Component?.[COMPONENT_TYPE_CODE] || {}),
        componentTypeCode: COMPONENT_TYPE_CODE,
        docResult,
      },
    },
  }
  writingStore.setStep2Info(nextStep2Info)
}

const handleGenerate = async () => {
  if (props.readOnly || !props.projectId || generating.value) {
    return
  }

  generating.value = true
  try {
    const result = await fetchGenerateInventiveIdea(props.projectId)
    if (!result) {
      throw new Error('后台未返回发明构思')
    }
    syncGeneratedResult(result)
  } catch (error) {
    const message = error?.message || '发明构思生成失败'
    ElMessage.error(message)
  } finally {
    generating.value = false
  }
}

watch(
  () => props.data?.docResult,
  () => {
    localDocResult.value = ''
  }
)

watch(
  () => [props.projectId, hasMap.value, props.readOnly],
  () => {
    if (props.readOnly || hasMap.value || hasAutoTriggered.value || !props.projectId) {
      return
    }
    hasAutoTriggered.value = true
    handleGenerate()
  },
  { immediate: true }
)
</script>

<style scoped lang="scss">
@import './common.scss';

.invent-idea-content {
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.idea-toolbar {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 12px;
}

.idea-block-list {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding-right: 2px;
}

.idea-block {
  padding: 12px 14px;
  margin-bottom: 10px;
  background: #f7faf8;
  border: 1px solid #dce8e1;
  border-radius: 8px;
  box-shadow: 0 6px 18px rgba(30, 64, 45, 0.06);
}

.idea-block:last-child {
  margin-bottom: 0;
}

.idea-block-primary {
  background: #f3f7fb;
  border-color: #d7e3ee;
}

.idea-block-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 9px;
}

.idea-block-index {
  flex: none;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  color: #ffffff;
  font-size: 12px;
  font-weight: 700;
  line-height: 22px;
  text-align: center;
  background: #2f4b40;
  border-radius: 11px;
}

.idea-block-primary .idea-block-index {
  background: #2d4664;
}

.idea-block-title {
  color: #21322b;
  font-size: 13px;
  font-weight: 700;
}

.idea-field-list {
  margin: 0;
}

.idea-field {
  display: grid;
  grid-template-columns: 72px minmax(0, 1fr);
  gap: 10px;
  padding: 8px 0;
  border-top: 1px solid rgba(59, 91, 75, 0.12);
}

.idea-field dt {
  color: #52645b;
  font-size: 12px;
  font-weight: 700;
  line-height: 20px;
  white-space: nowrap;
}

.idea-field dd {
  min-width: 0;
  margin: 0;
  color: #1f2937;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.58;
  word-break: break-word;
}

.idea-empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 260px;
}

.idea-empty-copy {
  line-height: 1.6;
}

.idea-empty-title {
  color: #1f2937;
  font-weight: 600;
}

.idea-empty-desc {
  color: #6b7280;
  font-size: 13px;
}

.invent-idea-component.compact {
  .application-component-title {
    padding: 12px 14px 8px;
    font-size: 14px;
    line-height: 20px;
  }

  .invent-idea-content {
    padding: 0 12px 12px;
  }

  .idea-toolbar {
    margin-bottom: 8px;
  }

  .idea-block {
    padding: 10px 11px;
    margin-bottom: 8px;
    border-radius: 6px;
    box-shadow: none;
  }

  .idea-block-head {
    margin-bottom: 6px;
  }

  .idea-block-index {
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    font-size: 10px;
    line-height: 18px;
  }

  .idea-block-title {
    font-size: 12px;
  }

  .idea-field {
    grid-template-columns: 58px minmax(0, 1fr);
    gap: 7px;
    padding: 6px 0;
  }

  .idea-field dt {
    font-size: 11px;
    line-height: 18px;
  }

  .idea-field dd {
    font-size: 12px;
    line-height: 1.5;
  }
}
</style>
